import { bool } from "prop-types";
import { useEffect, useMemo, useState } from "react";

import styled from "styled-components/macro";
import { backgroundColor, borderRadius, shadow } from "theme";
import { shadowVariants } from "theme/constants";

import { useFlocknoteAppState } from "Flocknote/state";

import { AnimatePresence } from "framer-motion";

import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap";

import useIsMobile from "Utilities/Hooks/useIsMobile";
import useUrlParams from "Utilities/Hooks/useUrlParams";
import makeItAnArray from "Utilities/makeItAnArray";

import { useUserChipContext } from "../../UserChip";
import { useAddProfileTabToUrl } from "../../stateHooks";

import Option from "./Option";
import { useRedirectToValidTab } from "./hooks";

const hiddenTabs = ["giving"];

function getInitialTabs(tabs) {
  if (tabs.length < 7) {
    return tabs;
  }
  return tabs.slice(0, 4);
}

function getMoreTabs(tabs) {
  if (tabs.length < 7) {
    return [];
  }
  return tabs.slice(4);
}

const SFnrVerticalGap = styled(FnrVerticalGap)`
  padding: 4px 1px 4px 7px;
  margin-block-start: 2px;
  ${borderRadius}
  ${backgroundColor}
  ${shadow}
`;

const UserMenu = ({ isSelected }) => {
  const { isChild } = useUserChipContext();

  const addProfileTabToUrl = useAddProfileTabToUrl();

  useRedirectToValidTab();

  const storedTab = makeItAnArray(
    JSON.parse(localStorage.getItem("userprofile_tab"))
  );
  const isMobile = useIsMobile(680);

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // availableProfileTabs are set by loadUserHandlerProfile function in userProfileHandler.js
  const [{ availableProfileTabs }] = useFlocknoteAppState();
  const visibleTabs = availableProfileTabs?.filter(
    (tab) => !hiddenTabs.includes(tab.action)
  );
  const initialTabs = getInitialTabs(visibleTabs);
  const moreTabs = getMoreTabs(visibleTabs);
  const { profileTab } = useUrlParams();

  const selectedTabInMoreSection = useMemo(
    () =>
      Boolean(
        moreTabs.find(({ action }) => action === (profileTab || storedTab[0]))
      ),
    [moreTabs, profileTab, storedTab]
  );

  const [showMoreClicked, setShowMoreClicked] = useState(
    selectedTabInMoreSection
  );

  const showMore = useMemo(() => {
    if (selectedTabInMoreSection || showMoreClicked) return true;

    return false;
  }, [selectedTabInMoreSection, showMoreClicked]);

  useEffect(() => {
    if (!isSelected) setShowMoreClicked(false);
    else if (selectedTabInMoreSection) setShowMoreClicked(true);
  }, [isSelected, selectedTabInMoreSection]);

  useEffect(() => {
    if (!profileTab && isSelected && !isMounted) {
      addProfileTabToUrl();
    }
  }, [isSelected, profileTab, isMounted, addProfileTabToUrl]);

  if (isMobile) return null;
  if (!isSelected) return null;

  return (
    <SFnrVerticalGap
      gap={4}
      $backgroundColor="grey"
      $shadowVariant={shadowVariants.insetDark}
      $borderRadius={10}
      data-cy="UserMenu_Container"
    >
      {initialTabs?.map((tab) => (
        <Option
          data-cy={`HouseholdProfile_UserChip_UserMenu_Option_${tab.title}`}
          key={tab.title}
          tab={tab}
          isChild={isChild}
          active={profileTab === tab.action}
        />
      ))}
      {!showMore && moreTabs.length > 0 && (
        <Option
          data-cy="HouseholdProfile_UserChip_UserMenu_Option_More"
          key="More"
          tab={{ action: "more", title: "More", icon: "ellipsis-h fas" }}
          isChild={isChild}
          onClick={() => setShowMoreClicked(true)}
        />
      )}
      <AnimatePresence>
        {showMore &&
          moreTabs?.map((tab) => (
            <Option
              data-cy={`HouseholdProfile_UserChip_UserMenu_Option_${tab.title}`}
              key={tab.title}
              tab={tab}
              isChild={isChild}
              active={profileTab === tab.action}
            />
          ))}
      </AnimatePresence>
    </SFnrVerticalGap>
  );
};

UserMenu.propTypes = {
  isSelected: bool,
};

export default UserMenu;
